import * as THREE from "three";
import { WORLD, paletteAt, heightT } from "./config.js";

/* Deterministic RNG so the tree is identical on every visit. */
export function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Space colonization: attractors pull growing nodes toward them.
 * Returns { nodes, segments } where each node is
 * { pos, parent, dist (path length from base), thickness }.
 */
export function colonize({ rng, targets }) {
  const INFLUENCE = 4.2;
  const KILL = 1.0;
  const STEP = 0.6;
  const MAX_ITER = 260;

  const attractors = [];

  // Main column: a tall, slightly widening cylinder of attractors.
  const columnCount = 520;
  for (let i = 0; i < columnCount; i++) {
    const t = rng();
    const y = 2 + t * (WORLD.canopyTop - 3);
    // Wider at canopy, narrow near base.
    const maxR = 1.6 + 4.6 * Math.pow(t, 0.8);
    const r = Math.sqrt(rng()) * maxR;
    const a = rng() * Math.PI * 2;
    attractors.push(new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r));
  }

  // Canopy burst: dense cluster at the very top.
  for (let i = 0; i < 220; i++) {
    const a = rng() * Math.PI * 2;
    const r = Math.sqrt(rng()) * 6.4;
    const y = WORLD.canopyTop - 4.5 + rng() * 5.5;
    attractors.push(new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r));
  }

  // Roots: inverted cone below ground.
  for (let i = 0; i < 200; i++) {
    const t = rng();
    const y = -0.5 + t * (WORLD.rootsBottom + 0.5); // 0 .. rootsBottom
    const maxR = 1.2 + 5.2 * Math.pow(Math.abs(y) / Math.abs(WORLD.rootsBottom), 0.9);
    const r = Math.sqrt(rng()) * maxR;
    const a = rng() * Math.PI * 2;
    attractors.push(new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r));
  }

  // Artwork targets: clusters of attractors so branches reach each work.
  for (const target of targets) {
    for (let i = 0; i < 26; i++) {
      attractors.push(
        new THREE.Vector3(
          target.x + (rng() - 0.5) * 1.7,
          target.y + (rng() - 0.5) * 1.7,
          target.z + (rng() - 0.5) * 1.7
        )
      );
    }
  }

  // Seed nodes: a short trunk column around y = 0 so growth can start
  // both upward (crown) and downward (roots).
  const nodes = [];
  const addNode = (pos, parent) => {
    const dist =
      parent === -1 ? 0 : nodes[parent].dist + pos.distanceTo(nodes[parent].pos);
    nodes.push({ pos, parent, dist, thickness: 1 });
    return nodes.length - 1;
  };

  let prev = addNode(new THREE.Vector3(0, 0.0, 0), -1);
  for (let i = 1; i <= 4; i++) {
    prev = addNode(new THREE.Vector3((rng() - 0.5) * 0.2, i * 0.5, (rng() - 0.5) * 0.2), prev);
  }
  addNode(new THREE.Vector3((rng() - 0.5) * 0.2, -0.5, (rng() - 0.5) * 0.2), 0);

  const dir = new THREE.Vector3();
  const sum = new THREE.Vector3();

  for (let iter = 0; iter < MAX_ITER && attractors.length; iter++) {
    // For each attractor find its nearest node within influence range.
    const pull = new Map(); // nodeIndex -> accumulated direction
    for (let ai = attractors.length - 1; ai >= 0; ai--) {
      const attr = attractors[ai];
      let best = -1;
      let bestD = INFLUENCE;
      for (let ni = 0; ni < nodes.length; ni++) {
        const d = attr.distanceTo(nodes[ni].pos);
        if (d < bestD) {
          bestD = d;
          best = ni;
        }
        if (d < KILL) {
          best = -2;
          break;
        }
      }
      if (best === -2) {
        attractors.splice(ai, 1);
        continue;
      }
      if (best >= 0) {
        dir.subVectors(attr, nodes[best].pos).normalize();
        if (!pull.has(best)) pull.set(best, new THREE.Vector3());
        pull.get(best).add(dir);
      }
    }

    if (!pull.size) break;

    for (const [ni, acc] of pull) {
      sum.copy(acc).normalize();
      // Slight upward bias above ground, downward below (roots).
      sum.y += nodes[ni].pos.y >= 0 ? 0.18 : -0.22;
      sum.normalize();
      const next = nodes[ni].pos.clone().addScaledVector(sum, STEP);
      // Small organic wobble.
      next.x += (rng() - 0.5) * 0.14;
      next.z += (rng() - 0.5) * 0.14;
      addNode(next, ni);
      if (nodes.length > 6000) break;
    }
    if (nodes.length > 6000) break;
  }

  // Pipe-model thickness: leaves = 1, parents accumulate.
  const childCount = new Array(nodes.length).fill(0);
  for (const n of nodes) if (n.parent >= 0) childCount[n.parent]++;
  const order = nodes
    .map((n, i) => i)
    .sort((a, b) => nodes[b].dist - nodes[a].dist); // farthest first
  const acc = new Array(nodes.length).fill(0);
  for (const i of order) {
    const w = Math.max(1, acc[i]);
    nodes[i].thickness = Math.pow(w, 0.5);
    if (nodes[i].parent >= 0) acc[nodes[i].parent] += w;
  }

  const segments = [];
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (n.parent < 0) continue;
    segments.push({
      a: nodes[n.parent].pos,
      b: n.pos,
      distA: nodes[n.parent].dist,
      distB: n.dist,
      thickness: (nodes[n.parent].thickness + n.thickness) * 0.5
    });
  }

  return { nodes, segments };
}

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uSize;
  uniform float uPixelRatio;
  uniform float uSparklePeriod;

  attribute float aSeed;
  attribute float aDist;
  attribute float aThick;
  attribute vec3 aColor;

  varying vec3 vColor;
  varying float vPulse;
  varying float vFade;
  varying float vSpark;

  void main() {
    vec3 p = position;

    // Gentle organic sway, stronger at thin branch tips.
    float sway = (1.0 - min(aThick, 1.0)) * 0.16;
    p.x += sin(uTime * 0.55 + aSeed * 6.2831 + position.y * 0.35) * sway;
    p.z += cos(uTime * 0.42 + aSeed * 6.2831 + position.y * 0.28) * sway;

    vec4 mv = modelViewMatrix * vec4(p, 1.0);

    // Light pulses travelling from the roots to the canopy along the
    // branch path. Two staggered wavefronts.
    float w1 = fract(aDist * 0.045 - uTime * 0.16);
    float w2 = fract(aDist * 0.045 - uTime * 0.16 + 0.5);
    float pulse = smoothstep(0.10, 0.0, w1) + smoothstep(0.10, 0.0, w2) * 0.6;
    vPulse = pulse;

    // Per-particle shimmer.
    float shimmer = 0.72 + 0.28 * sin(uTime * 1.7 + aSeed * 43.0);

    // Rare sparkle: each particle is scheduled a ~0.5s white peak on a
    // very long cycle, so across the whole cloud roughly one fires per
    // second. sin() gives a soft rise and fall.
    float sp = fract(aSeed + uTime / uSparklePeriod);
    float sx = sp * uSparklePeriod * 2.0; // 0..1 across the 0.5s window
    float spark = sin(min(sx, 1.0) * 3.14159265);
    vSpark = spark;

    float size = uSize * shimmer * (0.55 + 0.45 * min(aThick, 1.6))
      * (1.0 + pulse * 1.5 + spark * 2.6);
    gl_PointSize = size * uPixelRatio * (140.0 / -mv.z);
    gl_PointSize = min(gl_PointSize, (9.0 + spark * 9.0) * uPixelRatio);

    vColor = aColor;
    vFade = shimmer;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */ `
  precision highp float;

  varying vec3 vColor;
  varying float vPulse;
  varying float vFade;
  varying float vSpark;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float disc = smoothstep(0.5, 0.04, d);
    disc *= disc;

    vec3 col = vColor * (0.4 + vPulse * 1.2);
    // Pulses whiten toward the core.
    col += vec3(0.9, 1.0, 0.95) * vPulse * smoothstep(0.32, 0.0, d) * 0.3;
    // Sparkles peak to a bloom-hot white.
    col = mix(col, vec3(1.5, 1.55, 1.5), vSpark);

    gl_FragColor = vec4(col, disc * (vFade * 0.4 + vSpark * 0.6));
  }
`;

/**
 * Grow the tree and build its particle cloud.
 *
 * @param {Array<THREE.Vector3>} targets world positions branches should reach
 * @param {number} particleBudget total particles along branches
 * @returns {{ points: THREE.Points, update: (t:number)=>void,
 *             anchorFor: (target:THREE.Vector3)=>THREE.Vector3 }}
 */
export function buildTree({ targets = [], particleBudget = 60000, seed = 1234 }) {
  const rng = mulberry32(seed);
  const { nodes, segments } = colonize({ rng, targets });

  // Branch tips: nodes nobody grew from, reasonably far along a branch.
  const hasChild = new Array(nodes.length).fill(false);
  for (const n of nodes) if (n.parent >= 0) hasChild[n.parent] = true;
  const tips = [];
  for (let i = 0; i < nodes.length; i++) {
    if (!hasChild[i] && nodes[i].dist > 6) tips.push(nodes[i].pos.clone());
  }

  // Distribute particles over segments, weighted by length and girth.
  const weights = segments.map(
    (s) => s.a.distanceTo(s.b) * (0.6 + Math.min(s.thickness, 6) * 0.55)
  );
  const totalW = weights.reduce((a, b) => a + b, 0) || 1;

  const positions = new Float32Array(particleBudget * 3);
  const seeds = new Float32Array(particleBudget);
  const dists = new Float32Array(particleBudget);
  const thicks = new Float32Array(particleBudget);
  const colors = new Float32Array(particleBudget * 3);

  const tmp = new THREE.Vector3();
  const perp1 = new THREE.Vector3();
  const perp2 = new THREE.Vector3();
  const axis = new THREE.Vector3();

  let pi = 0;
  for (let si = 0; si < segments.length && pi < particleBudget; si++) {
    const s = segments[si];
    let count = Math.round((weights[si] / totalW) * particleBudget);
    if (si === segments.length - 1) count = particleBudget - pi;
    count = Math.min(count, particleBudget - pi);

    axis.subVectors(s.b, s.a).normalize();
    perp1.set(axis.y, axis.z, -axis.x).cross(axis).normalize();
    perp2.crossVectors(axis, perp1);

    const radius = 0.06 + Math.min(s.thickness, 6) * 0.085;

    for (let i = 0; i < count; i++, pi++) {
      const t = rng();
      tmp.lerpVectors(s.a, s.b, t);

      // Gaussian-ish radial scatter around the branch core.
      const rr = (rng() + rng() - 1) * radius;
      const aa = rng() * Math.PI * 2;
      tmp.addScaledVector(perp1, Math.cos(aa) * rr);
      tmp.addScaledVector(perp2, Math.sin(aa) * rr);

      positions[pi * 3] = tmp.x;
      positions[pi * 3 + 1] = tmp.y;
      positions[pi * 3 + 2] = tmp.z;
      seeds[pi] = rng();
      dists[pi] = s.distA + (s.distB - s.distA) * t;
      thicks[pi] = Math.min(s.thickness, 6) / 6;

      const [r, g, b] = paletteAt(heightT(tmp.y));
      colors[pi * 3] = r;
      colors[pi * 3 + 1] = g;
      colors[pi * 3 + 2] = b;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  geo.setAttribute("aDist", new THREE.BufferAttribute(dists, 1));
  geo.setAttribute("aThick", new THREE.BufferAttribute(thicks, 1));
  geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uSize: { value: 0.95 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
      // ~0.5s white peaks; with the whole cloud this yields roughly a
      // sparkle every second somewhere on the tree.
      // Each particle peaks white for ~0.5s once per this many seconds.
      // Scaled to the budget so, tree-wide, a sparkle fires roughly
      // every second regardless of tier.
      uSparklePeriod: { value: Math.max(1500, particleBudget / 3) }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });

  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;

  function update(t) {
    mat.uniforms.uTime.value = t;
  }

  /** Nearest grown node to a target — used to anchor artwork tethers. */
  function anchorFor(target) {
    let best = null;
    let bestD = Infinity;
    for (const n of nodes) {
      const d = n.pos.distanceTo(target);
      if (d < bestD) {
        bestD = d;
        best = n.pos;
      }
    }
    return best ? best.clone() : target.clone();
  }

  return { points, update, anchorFor, tips };
}

/**
 * Ambient drifting motes/fireflies filling the void around the trunk.
 */
export function buildMotes({ count = 2000, seed = 99 }) {
  const rng = mulberry32(seed);
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  const dists = new Float32Array(count);
  const thicks = new Float32Array(count);
  const colors = new Float32Array(count * 3);

  const spanY = WORLD.canopyTop - WORLD.rootsBottom + 14;
  for (let i = 0; i < count; i++) {
    const a = rng() * Math.PI * 2;
    const r = 2.5 + Math.pow(rng(), 0.6) * 22;
    const y = WORLD.rootsBottom - 7 + rng() * spanY;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = Math.sin(a) * r;
    seeds[i] = rng();
    dists[i] = rng() * 200; // decorrelate pulses
    thicks[i] = 0.1 + rng() * 0.25;

    const [cr, cg, cb] = paletteAt(heightT(y));
    const dim = 0.35 + rng() * 0.3;
    colors[i * 3] = cr * dim;
    colors[i * 3 + 1] = cg * dim;
    colors[i * 3 + 2] = cb * dim;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  geo.setAttribute("aDist", new THREE.BufferAttribute(dists, 1));
  geo.setAttribute("aThick", new THREE.BufferAttribute(thicks, 1));
  geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uSize: { value: 1.0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
      // Effectively never: motes keep their own shimmer, no sparkles.
      uSparklePeriod: { value: 1e9 }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });

  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;

  return {
    points,
    update(t) {
      mat.uniforms.uTime.value = t * 0.35;
    }
  };
}
